import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store';
import ProspectionTabs from '../../components/nouveauxAdherents/ProspectionTabs';
import EnhancedFilterForm from '../../components/nouveauxAdherents/EnhancedFilterForm';
import Pagination from '../../components/nouveauxAdherents/Pagination';
import EnhancedEstablishmentsTable from '../../components/nouveauxAdherents/EnhancedEstablishmentsTable';
import ExportButton from '../../components/nouveauxAdherents/ExportButton';
import { getFirstDayOfPreviousMonth, formatDateToYYYYMMDD } from '../../utils/dateUtils';
import NotificationPreferences from '../../components/NotificationPreferences';
import {  
  fetchNafCodes,
  selectCurrentView,
  selectEstablishments,
  selectIsLoading,
  selectTotalPages,
  selectCurrentPage,
  setCurrentPage,
  fetchNonClientCompaniesInClientGroups,
  selectNafCodes,
  selectIsLoadingNafCodes,
  fetchNewEstablishmentsInSector,
  fetchNewEstablishmentsOutOfSector,
  ProspectionViewValues,
  fetchNewCompanies,
  selectSortBy,
  selectSortOrder
} from '../../store/slices/nouveauxAdherentsSlice';

const NouveauxAdherentsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentView = useSelector(selectCurrentView);
  const establishments = useSelector(selectEstablishments);
  const isLoading = useSelector(selectIsLoading);
  const isLoadingNafCodes = useSelector(selectIsLoadingNafCodes);
  const totalPages = useSelector(selectTotalPages);
  const currentPage = useSelector(selectCurrentPage);
  const nafCodes = useSelector(selectNafCodes);
  const sortBy = useSelector(selectSortBy);
  const sortOrder = useSelector(selectSortOrder);
  
  // Number of items per page
  const itemsPerPage = 10;

  // Default from date is first day of previous month
  const defaultFromDate = formatDateToYYYYMMDD(new Date(getFirstDayOfPreviousMonth()));
  
  // State for custom filter form
  const [customFormData, setCustomFormData] = useState({
    dateFrom: defaultFromDate,
    dateTo: '',
    nafCode: '',
    nafCodes: [] as string[],
    postalCode: '',
    postalCodes: [] as string[],
    city: '',
    departement: '66', // Default departement 
    excludeMicroEntreprises: false,
    excludedProspectListIds: [] as number[],
    excludeProspectsMode: 'siret' as 'siret' | 'siren',
  });

  // Fetch NAF codes on mount if they're not loaded yet and not loading
  useEffect(() => {
    if (!nafCodes && !isLoadingNafCodes) {
      dispatch(fetchNafCodes());
    }
  }, [dispatch, nafCodes, isLoadingNafCodes]);

  // Function to fetch data based on current view
  const previousParamsRef = useRef<string>('');

  const buildParams = useCallback(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      dateFrom: customFormData.dateFrom,
      dateTo: customFormData.dateTo,
      departement: customFormData.departement,
      nafCode: customFormData.nafCode,
      nafCodes: customFormData.nafCodes,
      postalCode: customFormData.postalCode,
      postalCodes: customFormData.postalCodes,
      city: customFormData.city,
      excludeMicroEntreprises: customFormData.excludeMicroEntreprises,
      sortBy,
      sortOrder,
      excludedProspectListIds: customFormData.excludedProspectListIds,
      excludeProspectsMode: customFormData.excludeProspectsMode,
    }),
    [
      currentPage,
      customFormData,
      sortBy,
      sortOrder,
    ]
  );

  const fetchDataForCurrentView = useCallback(() => {
    const params = buildParams();
    // Include currentView in the serialization to detect view changes
    const serializedParams = JSON.stringify({ ...params, view: currentView });

    if (previousParamsRef.current === serializedParams) {
      return;
    }

    previousParamsRef.current = serializedParams;

    if (import.meta.env.DEV) {
      console.debug('[Prospection] Lancement de la recherche', params);
    }

    switch (currentView) {
      case ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES:
        dispatch(fetchNewCompanies(params));
        break;
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR:
        dispatch(fetchNewEstablishmentsInSector(params));
        break;
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR:
        dispatch(fetchNewEstablishmentsOutOfSector(params));
        break;
      case ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES:
        dispatch(fetchNonClientCompaniesInClientGroups(params));
        break;
      default:
        dispatch(fetchNewCompanies(params));
        break;
    }
  }, [buildParams, currentView, dispatch]);

  useEffect(() => {
    fetchDataForCurrentView();
  }, [fetchDataForCurrentView]);

  // Handle form submission
  const handleFilterSubmit = (values: any) => {
    setCustomFormData(values);
    dispatch(setCurrentPage(1));
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  // Handle sort change
  const handleSortChange = () => {
    // Reset to page 1 when sorting changes
    dispatch(setCurrentPage(1));
    // The sorting state is already updated in the table component
    // The useEffect will trigger a new fetch with the updated sorting
  };

  // Get title based on the current view
  const getViewTitle = () => {
    switch (currentView) {
      case ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES:
        return "Nouveaux établissements d'une nouvelle entreprise dans votre secteur";
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR:
        return "Nouveaux établissements dans votre secteur avec un siège dans votre secteur";
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR:
        return "Nouveaux établissements dans votre secteur avec un siège hors de votre secteur";
      case ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES:
        return "Entreprises non-clientes dans des groupes clients";
      default:
        return "Prospection";
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Prospection de nouveaux adhérents</h1>
      
      <NotificationPreferences type="prospection" />
      
      <ProspectionTabs />
      
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">{getViewTitle()}</h2>
        
        <EnhancedFilterForm 
          onSubmit={handleFilterSubmit}
          initialValues={customFormData}
          isLoading={isLoading || isLoadingNafCodes}
          currentView={currentView}
          nafCodes={nafCodes || []}
        />
      </div>
      
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Résultats</h3>
          <ExportButton formData={customFormData} />
        </div>
        
        <EnhancedEstablishmentsTable 
          establishments={establishments}
          nafCodes={nafCodes || []}
          isLoading={isLoading}
          onSortChange={handleSortChange}
        />
        
        {!isLoading && establishments && establishments.length > 0 && (
          <div className="mt-4 flex justify-center">
            <Pagination 
              currentPage={currentPage} 
              totalPages={totalPages} 
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NouveauxAdherentsPage; 